import {parseQueryProps, parseTagProps,} from "../utils/propsParser";
import {PropsParser} from "@webng/validations";
import {SharedThemeConfiguration} from "@webng-types/embedjs";
import globalSettings from "./globalSettings";
import ReactDOM from "react-dom/client";
import {notifyError} from "../analytics/notifyError";
import {ReactNode} from "react";
import {domContentLoaded} from "../utils/domContentLoaded";
import {parsePartialSharedThemeConfiguration} from "@webng-types/embedjs/shared";

function readMetaTags(prefix: string) {
  const metaTags = document.querySelectorAll(`meta[property^="${prefix}:"]`);
  const metaValues: Record<string, string> = {};

  metaTags.forEach(tag => {
    const property = tag.getAttribute('property');
    if (property) {
      const propertyName = property.substring(prefix.length + 1); // +1 for the colon
      const content = tag.getAttribute('content');
      if (content) {
        metaValues[propertyName] = content;
      }
    }
  });

  return metaValues
}

function readElementAttributes(element: HTMLElement) {
  const attrs: Record<string, string> = {};
  for (let attr of element.attributes) {
    attrs[attr.name] = attr.value;
  }
  return attrs;
}

export abstract class AbstractLiveblogElement<Configuration, TagProps, QueryProps, T> extends HTMLElement {
  private readonly wrapper: HTMLElement;
  private tagPropsParser: PropsParser<TagProps>;
  private queryPropsParser: PropsParser<QueryProps>;
  private props?: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>};
  private isRendered = false;
  private rerenderAfterHydrate = false
  protected liveblogComponent: T|undefined;
  private root: ReactDOM.Root|undefined

  protected constructor(tagPropsParser: PropsParser<TagProps>, queryPropsParser: PropsParser<QueryProps>) {
    super();
    this.wrapper = this;
    this.tagPropsParser = tagPropsParser;
    this.queryPropsParser = queryPropsParser;
  }

  connectedCallback() {
    this.props = this.parseProps();
    if (this.props) {
      if (globalSettings.useHydrate && this.useHydrate(this.props)) {
        this.hydrateWidget(this.wrapper, this.props, (ref) => this.liveblogComponent = ref);
      } else {
        this.renderWidget(this.wrapper, this.props, (ref) => this.liveblogComponent = ref);
      }
    }
  }

  disconnectedCallback() {
    this.isRendered = false;
    this.liveblogComponent = undefined;
    this.root?.unmount()
    this.root = undefined
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ) {
    if (this.isRendered && !this.rerenderAfterHydrate) {
      this.props = this.parseProps();
      this.renderWidget(this.wrapper, this.props, (ref) => this.liveblogComponent = ref);
    } else {
      this.rerenderAfterHydrate = true
    }
  }

  abstract parseConfigurationProps(metaValues: Record<string, string>, elementAttributes: Record<string, string>): Partial<Configuration>

  parseProps() {
    const metaValues = readMetaTags(this.tagName.toLowerCase());
    const elementAttributes = readElementAttributes(this);

    const tagProps = parseTagProps(this.tagPropsParser, this);
    const queryProps = parseQueryProps(this.queryPropsParser);

    const metaValues_sharedThemeConfiguration = parsePartialSharedThemeConfiguration(metaValues);
    const elementAttributes_sharedThemeConfiguration = parsePartialSharedThemeConfiguration(elementAttributes);

    return {
      tagProps,
      queryProps,
      overrideSharedConfiguration: Object.assign({}, metaValues_sharedThemeConfiguration, elementAttributes_sharedThemeConfiguration),
      overrideConfiguration: this.parseConfigurationProps(metaValues, elementAttributes)
    }
  }

  renderWidget(el: Element, props: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>}, refCallback: (ref: T) => void) {
    if(!this.root) {
      this.root = ReactDOM.createRoot(el, {
        onRecoverableError: (error: unknown, errorInfo: ReactDOM.ErrorInfo) => {
          notifyError(error, {
            digest: errorInfo.digest || "",
            componentStack: errorInfo.componentStack || ""
          })
        }
      })
    }
    this.root.render(this.renderImp(refCallback, props))
    this.isRendered = true;
    this.rerenderAfterHydrate = false;
  }

  hydrateWidget(el: Element, props: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>}, refCallback: (ref: T) => void) {
    return domContentLoaded(() => {
      const refCallbackWrapper = (ref: T) => {
        if(this.rerenderAfterHydrate) {
          setTimeout(() => {
            this.props = this.parseProps();
            this.renderWidget(this.wrapper, this.props, (ref) => this.liveblogComponent = ref);
          }, 100)
        } else {
          this.isRendered = true;
        }

        refCallback(ref)
      }

      // only send one hydration error
      let sentHydrationError = false

      this.root = ReactDOM.hydrateRoot(el, this.renderImp(refCallbackWrapper, props), {
        onRecoverableError: (error: unknown, errorInfo: ReactDOM.ErrorInfo) => {
          if(error instanceof Error && error.message.match(/Minified React error #(418|423|425)/)) {
            if(sentHydrationError) {
              return
            } else {
              sentHydrationError = true
            }
          }
          notifyError(error, {
            digest: errorInfo.digest || "",
            componentStack: errorInfo.componentStack || ""
          })
        }
      })
    })
  }

  abstract useHydrate(props: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>}): boolean

  abstract renderImp(ref: (r: T) => void, props: {tagProps: TagProps, queryProps: QueryProps, overrideSharedConfiguration: Partial<SharedThemeConfiguration>, overrideConfiguration: Partial<Configuration>}): ReactNode
}
